import { useEffect, useState } from 'react'
import { adminApi } from '../../api/adminApi'
import { Job } from '../../types'
import { toast } from 'react-hot-toast'
import { Loader2, CheckCircle, XCircle, ChevronLeft, ChevronRight } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
const STATUS_TABS = ['PENDING', 'ACTIVE', 'REJECTED', 'CLOSED'] as const
type StatusTab = typeof STATUS_TABS[number]

const STATUS_STYLE: Record<StatusTab, string> = {
  PENDING: 'badge bg-yellow-50 text-yellow-700',
  ACTIVE: 'badge bg-green-50 text-green-700',
  REJECTED: 'badge bg-red-50 text-red-700',
  CLOSED: 'badge bg-gray-100 text-gray-600',
}

export default function AdminJobsPage() {
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<StatusTab>('PENDING')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [actionLoading, setActionLoading] = useState<number | null>(null)
  const [rejectModal, setRejectModal] = useState<{ jobId: number } | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  useEffect(() => {
    loadJobs()
  }, [activeTab, page])

  const loadJobs = async () => {
    setLoading(true)
    try {
      const { data } = await adminApi.getJobs({ status: activeTab, page, size: 10 })
      const paged = data.data!
      setJobs(paged.content || [])
      setTotalPages(paged.totalPages)
    } catch {
      toast.error('Failed to load jobs')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (jobId: number) => {
    setActionLoading(jobId)
    try {
      await adminApi.approveJob(jobId)
      toast.success('Job approved!')
      loadJobs()
    } catch {
      toast.error('Failed to approve job')
    } finally {
      setActionLoading(null)
    }
  }

  const handleReject = async () => {
    if (!rejectModal || !rejectReason.trim()) return
    setActionLoading(rejectModal.jobId)
    try {
      await adminApi.rejectJob(rejectModal.jobId, rejectReason)
      toast.success('Job rejected')
      setRejectModal(null)
      setRejectReason('')
      loadJobs()
    } catch {
      toast.error('Failed to reject job')
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Job Posts</h1>

      {/* Status tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setPage(0) }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 size={24} className="animate-spin text-primary-600" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No {activeTab.toLowerCase()} jobs</div>
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <div key={job.id} className="card">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{job.title}</h3>
                    <span className={STATUS_STYLE[job.status as StatusTab]}>{job.status}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{job.company.name}</p>
                  <p className="text-sm text-gray-400 mt-0.5">
                    by {job.employee?.displayName || 'Employee'} ·{' '}
                    {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                  </p>
                </div>
                {job.referralFeePaise > 0 && (
                  <span className="badge badge-blue shrink-0">₹{job.referralFeePaise / 100}</span>
                )}
              </div>

              <p className="text-sm text-gray-600 mt-2 line-clamp-2">{job.description}</p>

              {job.skills.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {job.skills.slice(0, 5).map((s) => <span key={s} className="badge badge-gray">{s}</span>)}
                </div>
              )}

              {activeTab === 'PENDING' && (
                <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => handleApprove(job.id)}
                    disabled={actionLoading === job.id}
                    className="btn-primary text-xs flex items-center gap-1.5"
                  >
                    {actionLoading === job.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    Approve
                  </button>
                  <button
                    onClick={() => setRejectModal({ jobId: job.id })}
                    className="btn-secondary text-xs flex items-center gap-1.5 text-red-500 border-red-200 hover:bg-red-50"
                  >
                    <XCircle size={14} /> Reject
                  </button>
                </div>
              )}

              {job.status === 'REJECTED' && job.rejectionReason && (
                <p className="text-xs text-red-500 mt-2">Reason: {job.rejectionReason}</p>
              )}
            </div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="btn-secondary p-2">
                <ChevronLeft size={16} />
              </button>
              <span className="text-sm text-gray-600">Page {page + 1} of {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="btn-secondary p-2">
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Reject Job Post</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="input resize-none mb-4"
              placeholder="Reason for rejection (required)..."
            />
            <div className="flex gap-3">
              <button onClick={() => setRejectModal(null)} className="btn-secondary flex-1">Cancel</button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || !!actionLoading}
                className="flex-1 py-2.5 px-4 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
              >
                {actionLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
